import React, { FC, useState } from 'react';
import { Routes } from '@common/constants';
import moment, { Moment } from 'moment';

import { SpaDaysCategoryView } from '@features/spa/pages/SpaDays/SpaDaysCategoryView';
import { EntitiesListing } from '@features/EntitiesListing';

import { Property } from 'shared-types/Property';
import { JourneyParams } from 'shared-types/Common';
import { Spinner } from '@common/components';
import { useRouter } from 'shared-routing';
import { useCheckForArrivalDate } from '@common/hooks';
import { useGetAllSpaPackages } from '@features/spa/hooks/useGetAllSpaPackages';
import { useGetAvailableSpaPackages } from '@features/spa/hooks/useGetAvailableSpaPackages';
import { useGetSpaDaysPage } from '@features/spa/hooks/useGetSpaDaysPage';
import { useGetSpaCategories } from '@features/spa/hooks/useGetSpaCategories';
import { useGetSpaSettings } from '@features/spa';

type SpaDaysProps = {
  categoryId: number | null;
  property: Property;
  journeyParams: JourneyParams;
  isLoading: boolean;
  changeJourneyParam: (dateId: string, date?: string | Moment | number) => void;
  language: string;
};

export const SpaDays: FC<SpaDaysProps> = ({
  property,
  journeyParams,
  changeJourneyParam,
  categoryId = null,
  language,
}) => {
  const router = useRouter();
  const [categoryFilterId, setCategoryFilterId] = useState(categoryId);
  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.SPA_DAY_LIST,
  });

  const { spaDate } = journeyParams;

  const getSpaDateString = () => {
    if (!spaDate) return moment().format('yyyy-MM-DD');
    if (typeof spaDate === 'string') return spaDate;
    return spaDate.format('yyyy-MM-DD');
  };

  const { data: spaDaysAll = [] } = useGetAllSpaPackages(property.id, language);
  const { data: spaDaysPage = { title: '', content: '', heroBackgroundImage: { url: '' } } } =
    useGetSpaDaysPage(property.id, language);

  const { data: spaDays = [], isFetching } = useGetAvailableSpaPackages(
    property.id,
    { category: categoryFilterId, date: getSpaDateString() },
    language
  );

  const { data: spaPackagesCategories = [] } = useGetSpaCategories(
    property.id,
    'packages',
    language
  );
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const { isSpaDayCategoryViewVisible = false } = spaSettings || {};

  const availableSpaDays = (spaDays || [])
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((sd) => ({
      ...sd,
      imageGallery: [sd.image],
      linkRoute: Routes.SPA_DAY_DETAIL,
      linkParams: { id: sd.id },
    }));
  return (
    <>
      {isFetching && <Spinner showDelayMessage />}
      {isSpaDayCategoryViewVisible ? (
        <SpaDaysCategoryView
          categories={spaPackagesCategories}
          spaDaysPage={spaDaysPage}
          journeyParams={journeyParams}
          changeJourneyParam={changeJourneyParam}
        />
      ) : (
        <EntitiesListing
          entityRoute={Routes.SPA_DAY_DETAIL}
          entities={availableSpaDays}
          entitiesAll={spaDaysAll}
          entitiesPage={spaDaysPage}
          journeyParams={journeyParams}
          entitiesCategories={spaPackagesCategories}
          defaultTitle="app.spaDays"
          listingRoute={Routes.SPA_DAY_LIST}
          changeJourneyParam={changeJourneyParam}
          setCategoryFilterId={setCategoryFilterId}
          categoryFilterId={categoryFilterId}
        />
      )}
    </>
  );
};
